import { Aircraft } from "../../../types";
import { createFilteredOptions, FilteredAircraft } from "../../Settings/HangarSettings/aircraftFilterUtils";
import { CsvMatcher } from "./csvMatcher";

export type SuggestionConfidence = "high" | "medium" | null;

export interface ScoreBreakdown {
  manufacturerScore: number;
  modelScore: number;
  historicalScore: number;
  matchedHistoricalString?: string;
  manufacturerPenalty: number;
  method: "traditional" | "historical" | "combined";
}

export interface AircraftSuggestion {
  suggestedAircraft: Aircraft | null;
  score: number;
  confidence: SuggestionConfidence;
  breakdown?: ScoreBreakdown;
}

/**
 * Extended Aircraft type with historical FAA mappings.
 * Allows leveraging previously-mapped FAA strings for better suggestions.
 */
export interface AircraftWithHistory extends Aircraft {
  alreadyMatched?: string[];
}

/**
 * Confidence thresholds for hybrid CSV + fuzzy approach:
 * - High (0-10): Reserved for CSV verified matches only
 * - Medium (11-30): Strong fuzzy matches
 * - Low/No suggestion (31+): Weak fuzzy matches, not shown
 */
const CONFIDENCE_THRESHOLDS = {
  HIGH: 10,
  MEDIUM: 30,
} as const;

/**
 * Get the confidence level for a given score.
 */
const getConfidenceLevel = (score: number): SuggestionConfidence => {
  if (score <= CONFIDENCE_THRESHOLDS.HIGH) return "high";
  if (score <= CONFIDENCE_THRESHOLDS.MEDIUM) return "medium";
  return null;
};

/**
 * Generate an aircraft suggestion based on FAA manufacturer and model.
 *
 * Hybrid approach:
 * 1. Try CSV verified exact match first (HIGH confidence)
 * 2. Fall back to fuzzy matching (MEDIUM confidence)
 *
 * @param faaManufacturer - FAA manufacturer name (e.g., "BEECH", "CESSNA")
 * @param faaModel - FAA model name (e.g., "172M", "1900C")
 * @param allAircraft - Full list of available AirPlx aircraft
 * @param csvMatcher - Optional CSV matcher for verified exact matches
 * @returns Suggestion with aircraft, score, and confidence level
 */
export const getSuggestedAircraft = (
  faaManufacturer: string | null,
  faaModel: string | null,
  allAircraft: Aircraft[],
  csvMatcher?: CsvMatcher | null
): AircraftSuggestion => {
  // STAGE 0: Try CSV exact match first (highest confidence, verified data)
  if (csvMatcher && faaManufacturer && faaModel) {
    const csvMatch = csvMatcher.suggestMatch(faaManufacturer, faaModel);

    if (csvMatch) {
      // Find the aircraft in allAircraft list
      const aircraft = allAircraft.find(
        (a) =>
          a.make === csvMatch.airplx_make && a.model === csvMatch.airplx_model
      );

      if (aircraft) {
        return {
          suggestedAircraft: aircraft,
          score: 0,
          confidence: "high",
          breakdown: {
            manufacturerScore: 1.0,
            modelScore: 1.0,
            historicalScore: 0,
            matchedHistoricalString: `${faaManufacturer} / ${faaModel}`,
            manufacturerPenalty: 0,
            method: "csv-verified",
          },
        };
      }
    }
  }

  // No CSV match - fall back to fuzzy matching algorithm
  // Build search string from FAA data
  const searchParts: string[] = [];
  if (faaManufacturer) searchParts.push(faaManufacturer.trim());
  if (faaModel) searchParts.push(faaModel.trim());

  const searchString = searchParts.join(" ");

  // No search string means no suggestion
  if (!searchString) {
    return {
      suggestedAircraft: null,
      score: Infinity,
      confidence: null,
    };
  }

  // STAGE 1: Filter aircraft by manufacturer to reduce search space
  // This prevents cross-manufacturer matches (e.g., Eurocopter → ERCO)
  const candidatesByManufacturer = filterByManufacturer(
    faaManufacturer,
    allAircraft as AircraftWithHistory[]
  );

  // STAGE 2: Score models within filtered manufacturer list
  // Use only the model portion for scoring since manufacturer is already filtered
  const modelOnlySearchString = faaModel ? faaModel.trim() : searchString;

  const scoredAircraft: Array<{
    aircraft: Aircraft;
    score: number;
    breakdown: ScoreBreakdown;
  }> = [];

  for (const aircraft of candidatesByManufacturer) {
    // Calculate manufacturer match quality (0.0-1.0, higher is better)
    const manufacturerScore = matchManufacturer(
      faaManufacturer || "",
      aircraft.make || ""
    );

    // Calculate traditional score (comparing model only, manufacturer already filtered)
    const traditionalScore = calculateScore(aircraft, modelOnlySearchString);

    // Calculate historical score (comparing to previously-mapped FAA strings)
    const historicalResult = calculateHistoricalScore(
      aircraft,
      faaManufacturer,
      faaModel
    );
    let historicalScore = historicalResult.score;
    const matchedHistoricalString = historicalResult.matchedString;

    // CRITICAL: Verify the FAA model also matches the AIRCRAFT model
    // This prevents historical "305B" → aircraft "337" nonsense
    if (historicalScore < Infinity && faaModel && aircraft.model) {
      const faaVsAircraftSimilarity = compareModels(faaModel, aircraft.model);

      // If FAA model doesn't match aircraft model reasonably, ignore historical
      // Threshold 0.8: numbers must match exactly (1.0) or very close (0.8+ with letter diffs)
      if (faaVsAircraftSimilarity < 0.8) {
        historicalScore = Infinity;
      }
    }

    // Use the best (lowest) score from both methods
    let baseScore = Math.min(traditionalScore, historicalScore);
    const method =
      historicalScore < traditionalScore
        ? "historical"
        : traditionalScore < historicalScore
        ? "traditional"
        : "combined";

    // Adjust score based on manufacturer match quality
    // Better manufacturer match = lower (better) final score
    // Convert manufacturer score (0.0-1.0) to penalty (0-5)
    const manufacturerPenalty = (1.0 - manufacturerScore) * 5;
    const finalScore = baseScore + manufacturerPenalty;

    // QUALITY GATE: For fuzzy matches (non-CSV), require BOTH manufacturer AND model to be strong
    // This prevents weak matches from being suggested
    const isFuzzyMatch = baseScore > 0; // CSV has score 0, fuzzy has score > 0
    let passesQualityGate = true;

    if (isFuzzyMatch) {
      // Calculate model similarity for quality check
      const modelSimilarity = faaModel && aircraft.model
        ? compareModels(faaModel, aircraft.model)
        : 0.0;

      // Require both manufacturer ≥ 0.75 AND model ≥ 0.75 for fuzzy suggestions
      // This balances coverage vs quality
      if (manufacturerScore < 0.75 || modelSimilarity < 0.75) {
        passesQualityGate = false;
      }
    }

    if (finalScore !== Infinity && passesQualityGate) {
      scoredAircraft.push({
        aircraft,
        score: finalScore,
        breakdown: {
          manufacturerScore,
          modelScore: traditionalScore,
          historicalScore,
          matchedHistoricalString,
          manufacturerPenalty,
          method,
        },
      });
    }
  }

  // If no matches found, return null suggestion
  if (scoredAircraft.length === 0) {
    return {
      suggestedAircraft: null,
      score: Infinity,
      confidence: null,
    };
  }

  // Sort by score (ascending - lower is better)
  scoredAircraft.sort((a, b) => a.score - b.score);

  // Get the best match
  const bestMatch = scoredAircraft[0];
  const confidence = getConfidenceLevel(bestMatch.score);

  // Only return suggestion if confidence is high or medium
  if (confidence === null) {
    return {
      suggestedAircraft: null,
      score: bestMatch.score,
      confidence: null,
      breakdown: bestMatch.breakdown,
    };
  }

  return {
    suggestedAircraft: bestMatch.aircraft,
    score: bestMatch.score,
    confidence,
    breakdown: bestMatch.breakdown,
  };
};

/**
 * Calculate score for a single aircraft against search string.
 * This mirrors the logic in aircraftFilterUtils.ts scoreAircraft function.
 */
const calculateScore = (aircraft: Aircraft, searchString: string): number => {
  const make = aircraft.make || "";
  const model = aircraft.model || "";
  const fullName = `${make} ${model}`;

  const input = searchString.toLowerCase().trim();
  const inputTokens = input.split(/\s+/).filter((t) => t.length > 0);

  if (inputTokens.length === 0) return Infinity;

  // Score each token independently
  const tokenScores = inputTokens.map((token) => {
    const makeScore = scoreToken(token, make, true);
    const modelScore = scoreToken(token, model, false);
    const fullNameScore = scoreToken(token, fullName, false);

    return Math.min(makeScore, modelScore, fullNameScore);
  });

  // Sum all token scores
  const totalScore = tokenScores.reduce((sum, score) => sum + score, 0);

  return totalScore;
};

/**
 * Check if a token contains any numeric characters.
 * Numeric tokens require exact matching (no fuzzy matching).
 */
const isNumericToken = (token: string): boolean => {
  return /\d/.test(token);
};

/**
 * Score a single token against a target string.
 * Mirrors the logic from aircraftFilterUtils.ts scoreToken function.
 *
 * IMPORTANT: Tokens containing numbers (e.g., "222", "PA-34", "A320") require
 * EXACT matching only. No fuzzy/substring matching for numeric tokens to prevent
 * matching "222" with "212" or "PA-34-220T" with "PA-34-200T".
 */
const scoreToken = (
  token: string,
  target: string,
  isMakeField: boolean
): number => {
  const lowerTarget = target.toLowerCase();
  const lowerToken = token.toLowerCase();

  // STRICT NUMERIC MATCHING: If token or target contains numbers,
  // only allow exact match, prefix match, or word boundary match
  // (no substring/fuzzy matching to prevent "222" matching "212")
  const hasNumbers = isNumericToken(token) || isNumericToken(target);

  // Exact match
  if (lowerTarget === lowerToken) {
    return isMakeField ? 0 : 1;
  }

  // Prefix match (allowed for numeric tokens)
  if (lowerTarget.startsWith(lowerToken)) {
    return isMakeField ? 10 : 11;
  }

  // Word boundary match (allowed for numeric tokens like "58" in "Baron 58")
  const escapedToken = lowerToken.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const wordBoundaryRegex = new RegExp(`\\b${escapedToken}\\b`, "i");
  if (wordBoundaryRegex.test(lowerTarget)) {
    const matchIndex = lowerTarget.search(wordBoundaryRegex);
    const score = isMakeField ? 20 + matchIndex : 21 + matchIndex;

    // For numeric tokens, return this score - no further fuzzy matching
    if (hasNumbers) {
      return score;
    }

    // For non-numeric, continue to allow better matches below
    return score;
  }

  // For numeric tokens, STOP HERE - no substring/fuzzy matching allowed
  if (hasNumbers) {
    return Infinity;
  }

  // Substring match (only for non-numeric tokens)
  const substringIndex = lowerTarget.indexOf(lowerToken);
  if (substringIndex !== -1) {
    return isMakeField ? 30 + substringIndex : 31 + substringIndex;
  }

  // Fuzzy match (Levenshtein distance) - only for tokens of 4+ characters
  if (lowerToken.length >= 4) {
    const distance = levenshtein(lowerToken, lowerTarget);
    const threshold = lowerToken.length <= 5 ? 2 : 4;
    if (distance <= threshold) {
      return isMakeField ? 40 + distance : 41 + distance;
    }
  }

  // No match
  return Infinity;
};

/**
 * Calculate Levenshtein distance between two strings.
 * Copied from hooks/utils.ts to avoid circular dependencies.
 */
const levenshtein = (a: string, b: string): number => {
  const an = a.length;
  const bn = b.length;
  const matrix: number[][] = [];

  if (an === 0) return bn;
  if (bn === 0) return an;

  for (let i = 0; i <= bn; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= an; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= bn; i++) {
    for (let j = 1; j <= an; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[bn][an];
};

/**
 * Corporate suffixes to strip during normalization
 */
const CORPORATE_SUFFIXES = [
  "INC",
  "CORP",
  "LLC",
  "CO",
  "LTD",
  "S A S",
  "ACFT OF CANADA LTD",
  "AIRCRAFT",
  "AVIATION",
  "INDUSTRIES",
  "INDUSTRIE",
  "FLUGZEUGWERKE", // German aircraft works
  "SPA", // Italian company suffix
  "AG", // German company suffix
];

/**
 * Manufacturer alias map - maps normalized manufacturer names to their common variations.
 * This allows "CESSNA AIRCRAFT CO" to match "Cessna", "Reims/cessna" to match "Cessna", etc.
 */
const MANUFACTURER_ALIASES: Record<string, string[]> = {
  cessna: ["cessna", "reims", "reims cessna", "textron aviation"],
  beechcraft: ["beech", "beechcraft", "hawker beechcraft", "raytheon"],
  piper: ["piper", "new piper", "piper aircraft"],
  boeing: ["boeing", "boeing co", "mcdonnell douglas"],
  airbus: ["airbus", "airbus industrie", "airbus s a s"],
  bombardier: ["bombardier", "learjet", "gates learjet", "canadair"],
  eurocopter: ["eurocopter", "aerospatiale", "american eurocopter"],
  "bell helicopter": ["bell", "bell helicopter", "bell helicopter textron"],
  cirrus: ["cirrus", "cirrus design", "cirrus aircraft"],
  mooney: ["mooney", "mooney aircraft"],
  diamond: ["diamond", "diamond aircraft"],
  "de havilland": ["de havilland", "dehavilland", "de havilland canada"],
  "american aviation": ["american aviation", "grumman american"],
  erco: ["erco", "engineering and research"],
  luscombe: ["luscombe", "silvaire"],
  champion: ["champion", "american champion"],
  "aero commander": ["aero commander", "commander", "rockwell"],
  "aviat aircraft": ["aviat", "aviat aircraft"],
  maule: ["maule", "maule aircraft"],
  "air tractor": ["air tractor"],
  pilatus: ["pilatus", "pilatus aircraft", "pilatus flugzeugwerke"],
  "md helicopters": ["md helicopters", "mcdonnell douglas helicopters"],
  agusta: ["agusta", "agustawestland", "leonardo"],
  "british aerospace": ["british aerospace", "bae", "hawker siddeley"],
};

/**
 * Normalize an FAA string for comparison by:
 * - Converting to lowercase
 * - Removing extra whitespace
 * - Removing corporate suffixes
 * - Normalizing punctuation (remove periods, convert / to space)
 */
const normalizeFaaString = (str: string): string => {
  let normalized = str.toLowerCase().trim();

  // Remove corporate suffixes
  for (const suffix of CORPORATE_SUFFIXES) {
    const lowerSuffix = suffix.toLowerCase();
    if (normalized.endsWith(` ${lowerSuffix}`)) {
      normalized = normalized.slice(0, -(lowerSuffix.length + 1));
    }
  }

  // Normalize punctuation
  normalized = normalized
    .replace(/\./g, "") // Remove periods
    .replace(/\//g, " ") // Convert slashes to spaces
    .replace(/\s+/g, " ") // Collapse multiple spaces
    .trim();

  return normalized;
};

/**
 * Normalize a manufacturer name for matching.
 * Similar to normalizeFaaString but more aggressive for manufacturer-specific comparisons.
 */
const normalizeManufacturer = (manufacturer: string): string => {
  if (!manufacturer) return "";

  let normalized = manufacturer.toLowerCase().trim();

  // Remove corporate suffixes
  for (const suffix of CORPORATE_SUFFIXES) {
    const lowerSuffix = suffix.toLowerCase();
    // Try with space prefix
    if (normalized.endsWith(` ${lowerSuffix}`)) {
      normalized = normalized.slice(0, -(lowerSuffix.length + 1));
    }
    // Try without space (handles cases like "CESSNAAIRCRAFT")
    if (normalized.endsWith(lowerSuffix)) {
      normalized = normalized.slice(0, -lowerSuffix.length);
    }
  }

  // Normalize punctuation
  normalized = normalized
    .replace(/\./g, "") // Remove periods
    .replace(/\//g, " ") // Convert slashes to spaces (handles "Reims/cessna")
    .replace(/-/g, " ") // Convert hyphens to spaces
    .replace(/\s+/g, " ") // Collapse multiple spaces
    .trim();

  return normalized;
};

/**
 * Match a manufacturer name against known aliases and fuzzy matching.
 * Returns a score between 0.0 (no match) and 1.0 (perfect match).
 *
 * Scoring:
 * - Exact match: 1.0
 * - Alias match: 0.9
 * - Fuzzy match (≥80% similarity): 0.8-0.99
 * - Fuzzy match (≥70% similarity): 0.7-0.79
 * - No match: 0.0
 */
const matchManufacturer = (
  faaManufacturer: string,
  aircraftMake: string
): number => {
  if (!faaManufacturer || !aircraftMake) return 0.0;

  const normalizedFaa = normalizeManufacturer(faaManufacturer);
  const normalizedAircraft = normalizeManufacturer(aircraftMake);

  // Exact match after normalization
  if (normalizedFaa === normalizedAircraft) {
    return 1.0;
  }

  // Check if either contains the other (handles single word vs multi-word)
  if (normalizedFaa.includes(normalizedAircraft) || normalizedAircraft.includes(normalizedFaa)) {
    return 0.95;
  }

  // Token-based partial matching (handles "Hawker Beechcraft" vs "Beechcraft")
  const faaTokens = normalizedFaa.split(/\s+/).filter(t => t.length > 2);
  const aircraftTokens = normalizedAircraft.split(/\s+/).filter(t => t.length > 2);

  // Check if any significant tokens match
  const hasMatchingToken = faaTokens.some(ft =>
    aircraftTokens.some(at => ft === at || ft.includes(at) || at.includes(ft))
  );

  if (hasMatchingToken) {
    return 0.85;
  }

  // Check alias map
  for (const [canonical, aliases] of Object.entries(MANUFACTURER_ALIASES)) {
    const faaInAliases = aliases.some(alias =>
      normalizedFaa.includes(alias) || alias.includes(normalizedFaa)
    );
    const aircraftInAliases = aliases.some(alias =>
      normalizedAircraft.includes(alias) || alias.includes(normalizedAircraft)
    );

    if (faaInAliases && aircraftInAliases) {
      return 0.9;
    }
  }

  // Fuzzy matching using Levenshtein distance
  const distance = levenshtein(normalizedFaa, normalizedAircraft);
  const maxLength = Math.max(normalizedFaa.length, normalizedAircraft.length);
  const similarity = 1 - distance / maxLength;

  // Map similarity to score range (slightly relaxed thresholds)
  if (similarity >= 0.75) {
    return 0.75 + (similarity - 0.75) * 1.0; // 0.75-1.0
  } else if (similarity >= 0.65) {
    return 0.65 + (similarity - 0.65) * 0.9; // 0.65-0.74
  } else if (similarity >= 0.5) {
    return 0.5 + (similarity - 0.5) * 0.8; // 0.5-0.64
  }

  return 0.0;
};

/**
 * Filter aircraft list to only those with matching manufacturers.
 * Returns filtered list if matches found, otherwise returns full list (safety net).
 */
const filterByManufacturer = (
  faaManufacturer: string | null,
  allAircraft: AircraftWithHistory[]
): AircraftWithHistory[] => {
  // No manufacturer provided - return all
  if (!faaManufacturer) {
    return allAircraft;
  }

  // Score each aircraft's manufacturer
  const scoredAircraft = allAircraft.map(aircraft => ({
    aircraft,
    manufacturerScore: matchManufacturer(faaManufacturer, aircraft.make || ""),
  }));

  // Filter to matches with score ≥ 0.70 (70% threshold)
  // Quality gate in main algorithm ensures strong matches
  const matches = scoredAircraft
    .filter(s => s.manufacturerScore >= 0.70)
    .map(s => s.aircraft);

  // NO FALLBACK: If manufacturer doesn't match, return empty list
  // This prevents nonsense matches like DJI → Piper
  return matches;
};

/**
 * Model components: separate numbers and letters for sophisticated matching
 */
interface ModelComponents {
  numbers: number[];
  letters: string[];
}

/**
 * Extract components from a model string.
 * Separates numeric sequences from text for independent comparison.
 *
 * Examples:
 * - "172M" → numbers:[172], letters:["M"]
 * - "PA-34-220T" → numbers:[34,220], letters:["PA","T"]
 * - "EC120B Colibri" → numbers:[120], letters:["EC","B","Colibri"]
 * - "Baron 58" → numbers:[58], letters:["Baron"]
 */
const extractComponents = (model: string): ModelComponents => {
  const normalized = normalizeModel(model);

  // Extract all number sequences
  const numberMatches = normalized.match(/\d+/g);
  const numbers = numberMatches ? numberMatches.map(n => parseInt(n, 10)) : [];

  // Extract all letter sequences
  const letterMatches = normalized.match(/[a-z]+/g);
  const letters = letterMatches || [];

  return { numbers, letters };
};

/**
 * Compare two number arrays with strict exact matching.
 * ALL numbers must match EXACTLY - no partial credit for similar digits.
 * This prevents "305" from matching "337" or "212" from matching "412".
 *
 * Returns score 0.0-1.0 (higher is better).
 */
const compareNumberArrays = (arr1: number[], arr2: number[]): number => {
  // Both empty = perfect match
  if (arr1.length === 0 && arr2.length === 0) return 1.0;

  // One empty = no match
  if (arr1.length === 0 || arr2.length === 0) return 0.0;

  // Different lengths = different aircraft (172 vs PA-34-220)
  if (arr1.length !== arr2.length) return 0.0;

  // ALL numbers must match EXACTLY
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return 0.0; // ANY number difference = no match
    }
  }

  // All numbers match exactly
  return 1.0;
};

/**
 * Compare letter components with fuzzy matching.
 * Returns score 0.0-1.0 (higher is better).
 */
const compareLetters = (arr1: string[], arr2: string[]): number => {
  // Both empty = perfect match
  if (arr1.length === 0 && arr2.length === 0) return 1.0;

  // One empty = partial score (numbers matched, no letters to compare)
  if (arr1.length === 0 || arr2.length === 0) return 0.7;

  // Join into strings and compare
  const str1 = arr1.join(" ");
  const str2 = arr2.join(" ");

  if (str1 === str2) return 1.0;

  // Fuzzy comparison
  const distance = levenshtein(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);
  const similarity = 1 - distance / maxLength;

  return Math.max(0, similarity);
};

/**
 * Compare two model strings using component-based matching.
 * Combines numeric position-weighted comparison with letter fuzzy matching.
 *
 * Returns a score 0.0-1.0 (higher is better).
 * - 1.0 = Perfect match
 * - 0.9+ = Same numbers, minor letter differences (HIGH confidence)
 * - 0.7-0.89 = Major numbers match, some digit differences (MEDIUM confidence)
 * - 0.5-0.69 = Partial match (LOW confidence)
 * - <0.5 = Too different (NO MATCH)
 */
const compareModels = (model1: string, model2: string): number => {
  const norm1 = normalizeModel(model1);
  const norm2 = normalizeModel(model2);

  // Exact match
  if (norm1 === norm2) return 1.0;

  // Extract components
  const comp1 = extractComponents(norm1);
  const comp2 = extractComponents(norm2);

  // For text-only models (no numbers), use simple fuzzy matching
  if (comp1.numbers.length === 0 && comp2.numbers.length === 0) {
    const distance = levenshtein(norm1, norm2);
    const maxLength = Math.max(norm1.length, norm2.length);
    return Math.max(0, 1 - distance / maxLength);
  }

  // Compare numeric components (80% weight)
  const numericScore = compareNumberArrays(comp1.numbers, comp2.numbers);

  // Compare letter components (20% weight)
  const letterScore = compareLetters(comp1.letters, comp2.letters);

  // Weighted combination
  return numericScore * 0.8 + letterScore * 0.2;
};

/**
 * Normalize a model string for comparison.
 * Less aggressive than normalizeFaaString - preserves model structure.
 */
const normalizeModel = (model: string): string => {
  return model
    .toLowerCase()
    .trim()
    .replace(/\([^)]*\)/g, "") // Remove parentheses and contents (variant designations like "(L20A)")
    .replace(/\./g, "") // Remove periods
    .replace(/\s+/g, " ") // Collapse whitespace
    .trim();
};

/**
 * Calculate similarity between two FAA strings.
 * Returns a distance score between 0 (identical) and Infinity (no match).
 *
 * CRITICAL FIX: Compares ONLY the model portions (after " / "), not full strings.
 * This prevents "CESSNA / 162" from matching "CESSNA / 182" just because
 * the manufacturer portions are identical.
 *
 * Uses component-based matching with strict numeric requirements.
 */
const compareFaaStrings = (str1: string, str2: string): number => {
  // Extract model portions (everything after " / ")
  // "CESSNA AIRCRAFT CO / 162" → "162"
  const model1 = str1.includes(" / ") ? str1.split(" / ")[1] : str1;
  const model2 = str2.includes(" / ") ? str2.split(" / ")[1] : str2;

  // Compare models using component-based matching (returns 0.0-1.0, higher is better)
  const similarity = compareModels(model1, model2);

  // Convert similarity score (0.0-1.0) to distance score (0-Infinity, lower is better)
  // 1.0 similarity → 0 distance (perfect match)
  // 0.9 similarity → 1 distance (excellent match)
  // 0.5 similarity → 10 distance (weak match)
  // 0.0 similarity → Infinity (no match)
  if (similarity >= 0.9) {
    return (1.0 - similarity) * 10; // 0-1 range
  } else if (similarity >= 0.5) {
    return (1.0 - similarity) * 20; // 1-10 range
  } else {
    return Infinity; // Too weak
  }
};

/**
 * Calculate score based on historical FAA string matches.
 * Compares the incoming FAA string against all previously-mapped FAA strings
 * for this aircraft, returning the best (lowest) score and the matched string.
 */
const calculateHistoricalScore = (
  aircraft: AircraftWithHistory,
  faaManufacturer: string | null,
  faaModel: string | null
): { score: number; matchedString?: string } => {
  // No historical data available
  if (!aircraft.alreadyMatched || aircraft.alreadyMatched.length === 0) {
    return { score: Infinity };
  }

  // Build incoming FAA string
  const incomingParts: string[] = [];
  if (faaManufacturer) incomingParts.push(faaManufacturer.trim());
  if (faaModel) incomingParts.push(faaModel.trim());
  const incomingFaaString = incomingParts.join(" / ");

  if (!incomingFaaString) {
    return { score: Infinity };
  }

  // Compare against each historical FAA string
  let bestScore = Infinity;
  let bestMatchedString: string | undefined;

  for (const historicalFaaString of aircraft.alreadyMatched) {
    const score = compareFaaStrings(incomingFaaString, historicalFaaString);
    if (score < bestScore) {
      bestScore = score;
      bestMatchedString = historicalFaaString;
    }
  }

  return { score: bestScore, matchedString: bestMatchedString };
};
